import { useCallback } from "react";
import { useForm, useWatch, useFormState } from "react-hook-form";
import { QualityTabs } from "@design-lab/features/design-artwork";
import { trackPrintOptionClicked } from "@design-lab/shared/lib/analytics";
import { type models } from "jiffy-api";
import type { TransferFormData, TransferFormControl } from "@design-lab/entities/transfers-lab";

interface TransferQualitySectionProps {
  formControl: TransferFormControl;
}

export function TransferQualitySection({ formControl }: TransferQualitySectionProps) {
  // Use the form control from the selected artwork
  const form = useForm<TransferFormData>({
    formControl
  });

  // Watch form values
  const enhancementTier = useWatch({
    control: form.control,
    name: "enhancementTier"
  });

  const removeBackground = useWatch({
    control: form.control,
    name: "removeBackground"
  });

  const jiffyDesignServices = useWatch({
    control: form.control,
    name: "jiffyDesignServices"
  });

  const enhancementRequestNote = useWatch({
    control: form.control,
    name: "enhancementRequestNote"
  });

  // Get form state for validation
  const fieldState = useFormState({
    control: form.control,
    name: "enhancementRequestNote"
  });
  const instructionsError = fieldState.errors?.enhancementRequestNote?.message;

  // Register the instructions field
  const instructionsField = form.register("enhancementRequestNote", {
    validate: value => {
      if (jiffyDesignServices && (!value || !value.trim())) {
        return "Comment is required";
      }
      return true;
    }
  });

  const handleQualityChange = useCallback(
    (value: models.cart.madeAdd.EnhancementTier) => {
      // Track analytics
      const printOptions = {
        print_original: "print_as_is",
        full_enhancements: "print_perfect",
        custom: "artist_edit"
      } as const;

      const printOption = printOptions[value];
      if (printOption) {
        // Special logging for transfers - same as design lab for now
        trackPrintOptionClicked(printOption);
      }

      // Update form values
      form.setValue("enhancementTier", value);

      // If switching to "Print As Is", disable remove background and jiffy design services
      if (value === "print_original") {
        form.setValue("removeBackground", false);
        form.setValue("jiffyDesignServices", false);
        form.setValue("enhancementRequestNote", "");
      }
    },
    [form]
  );

  const handleRemoveBackgroundChange = useCallback(
    (checked: boolean) => {
      form.setValue("removeBackground", checked);
      // Note: For transfers, we don't track removeBackground changes
      // since it's not available in the UI
    },
    [form]
  );

  const handleJiffyDesignServicesChange = useCallback(
    (checked: boolean) => {
      form.setValue("jiffyDesignServices", checked);
      if (checked) {
        form.setValue("enhancementTier", "full_enhancements");
      }
      // No special tracking needed for transfers
    },
    [form]
  );

  const handleEnhancementRequestNoteChange = useCallback(
    (value: string) => {
      form.setValue("enhancementRequestNote", value);
    },
    [form]
  );

  return (
    <div className="mt-6">
      <h3 className="mb-4 text-base font-semibold">Choose print quality</h3>
      <QualityTabs
        value={enhancementTier}
        onChange={handleQualityChange}
        canRemoveBackground={false}
        showRefineArtwork={false}
        showPrintPerfectTooltip={false}
        canEditCutoutModal={false}
        removeBackground={removeBackground || false}
        jiffyDesignServices={jiffyDesignServices || false}
        enhancementRequestNote={enhancementRequestNote}
        editImageManual={false} // Transfers don't have manual edits
        instructionsError={instructionsError}
        placementSide="front" // Transfers are always front
        onRemoveBackgroundChange={handleRemoveBackgroundChange}
        onJiffyDesignServicesChange={handleJiffyDesignServicesChange}
        onEnhancementRequestNoteChange={handleEnhancementRequestNoteChange}
        enhancementRequestNoteField={instructionsField}
      />
    </div>
  );
}
